import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { getPostBySlug } from "@/data/blogPosts";
import LeadCaptureForm from "@/components/LeadCaptureForm";
import Footer from "@/components/Footer";
import { usePageMeta } from "@/seo";

function renderContent(content) {
  // Simple markdown-like rendering for bold text and italics
  const lines = content.split('\n\n');
  
  return lines.map((paragraph, idx) => {
    // Handle bold text **text**
    let processed = paragraph.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-[#1A221E]">$1</strong>');
    // Handle italic text *text*
    processed = processed.replace(/\*(.+?)\*/g, '<em class="italic">$1</em>');
    
    // Check if it's a disclaimer (starts with italic marker in original)
    const isDisclaimer = paragraph.startsWith('*This article');
    const isSignature = paragraph.startsWith('*—');
    
    if (isDisclaimer || isSignature) {
      return (
        <p 
          key={idx} 
          className="font-body text-sm text-[#5C6A61] leading-relaxed italic border-t border-[#E4DCCF] pt-6 mt-8"
          dangerouslySetInnerHTML={{ __html: processed }}
        />
      );
    }
    
    return (
      <p 
        key={idx} 
        className="font-body text-base sm:text-lg text-[#3A4F41] leading-relaxed"
        dangerouslySetInnerHTML={{ __html: processed }}
      />
    );
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  usePageMeta(`/blog/${slug}`);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="blog-post-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-[#E4DCCF]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="font-heading text-xl text-[#1A221E] tracking-tight">
            Dr. Greenspan
          </Link>
          <nav className="hidden sm:flex items-center gap-8">
            <Link to="/#about" className="font-body text-sm text-[#5C6A61] hover:text-[#1A221E] transition-colors">About</Link>
            <Link to="/#services" className="font-body text-sm text-[#5C6A61] hover:text-[#1A221E] transition-colors">Services</Link>
            <Link to="/blog" className="font-body text-sm text-[#C87B5D] font-medium">Blog</Link>
            <Link to="/#contact" className="font-body text-sm text-[#5C6A61] hover:text-[#1A221E] transition-colors">Contact</Link>
            <Link 
              to="/book-online" 
              className="font-body text-sm font-medium text-white bg-[#C87B5D] hover:bg-[#b56a4e] px-5 py-2 rounded-full transition-colors"
            >
              Book Online
            </Link>
          </nav>
        </div>
      </header>

      {/* Back Link */}
      <div className="max-w-3xl mx-auto px-6 lg:px-8 pt-8">
        <Link 
          to="/blog" 
          className="inline-flex items-center gap-2 font-body text-sm text-[#5C6A61] hover:text-[#C87B5D] transition-colors"
          data-testid="back-to-blog"
        >
          <ArrowLeft size={16} />
          Back to Articles
        </Link>
      </div>

      {/* Article */}
      <article className="py-12 px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <header className="mb-12">
            <p className="font-body text-xs tracking-[0.2em] uppercase font-bold text-[#C87B5D] mb-4">
              Wellness Article
            </p>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-[#1A221E] tracking-tight leading-tight" data-testid="post-title">
              {post.title}
            </h1>
          </header>

          <div className="prose-custom space-y-6" data-testid="post-content">
            {renderContent(post.content)}
          </div>
        </div>
      </article>

      {/* Lead Capture */}
      <section className="py-16 px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <LeadCaptureForm />
          <p className="font-body text-sm text-[#5C6A61] text-center mt-8" data-testid="blog-marion-link">
            <Link to="/marion" className="text-[#3A4F41] underline underline-offset-4">Dr. Greenspan practices in Marion, NC — hours and directions.</Link>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
